import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { SceneSystem } from './SceneSystem';
import { VEHICLE_SCALE, VEHICLE_SCALE_OVERRIDE, MODEL_ROTATION_OFFSET, VEHICLE_MODEL_IDS } from '../core/constants';
import { applyTronMaterial, getCurrentStyle } from '../core/tron-material';
import { pauseClock } from '../core/pause';
import { assetUrl } from '../../lib/assets';

type LaneState = 'LEFT' | 'CHANGING_RIGHT' | 'RIGHT' | 'CHANGING_LEFT';

interface VehicleState {
  mesh: THREE.Object3D;
  wheelMeshes: THREE.Object3D[];
  modelId: string;
  label: string;
  labelEl: HTMLDivElement | null;
  route: [number, number][];
  startTime: number;
  duration: number;
  progress: number;
  speed: number;
  smoothBearing: number;
  currentPosition: [number, number];
  currentBearing: number;
  pingPong: boolean;
  forward: boolean;
  animationId: number;
  wheelRotation: number;
  tilt: number;
  prevBearing: number;
  lastTime: number;
  vehicleLength: number;
  // Lane management
  laneState: LaneState;
  laneOffset: number;
  laneChangeProgress: number;
  laneChangeStartOffset: number;
  stuckTimer: number;
  overtakeTargetId: string | null;
  overtakeCooldown: number;
  overtakeTimer: number;
}

const VEHICLE_HALF_LENGTH: Record<string, number> = {
  'simpoly-tanker': 23,
  'simpoly-tanker2': 12,
  'simpoly-dump': 13,
  'simpoly-pickup': 8,
  'simpoly-semi': 11,
  'simpoly-mixer': 13,
  'kenney-sedan': 7,
  'kenney-suv': 7.5,
  'kenney-taxi': 7.5,
  'kenney-van': 7,
  'kenney-delivery': 9,
  'kenney-truck': 8,
  'kenney-truck-flat': 7.5,
  'kenney-firetruck': 9.5,
  'kenney-garbage-truck': 9.5,
  'kenney-police': 8.5,
};

const LEFT_LANE = 7;
const RIGHT_LANE = -7;
const LANE_CHANGE_TIME = 1.8;
const STUCK_TIME = 1.2;
const SAFE_GAP = 10;
const LOOK_AHEAD = 80;
const ONCOMING_RANGE = 100;
const CLEAR_MARGIN = 1.5;
const OVERTAKE_COOLDOWN = 3.5;
const TURN_THRESHOLD = 25;
const HARD_MIN_DIST = 5;

export class VehicleSystem {
  private scene: SceneSystem;
  private loader = new GLTFLoader();
  private modelCache = new Map<string, THREE.Group>();
  private vehicles = new Map<string, VehicleState>();
  private animating = true;
  private cityRouteFunc: ((fromX: number, fromZ: number, toX: number, toZ: number) => Array<[number, number]> | null) | null = null;

  constructor(scene: SceneSystem) {
    this.scene = scene;
  }

  setCityRouter(routeFunc: (fromX: number, fromZ: number, toX: number, toZ: number) => Array<[number, number]> | null): void {
    this.cityRouteFunc = routeFunc;
  }

  async preload(onProgress?: (loaded: number, total: number) => void): Promise<void> {
    let loaded = 0;
    onProgress?.(0, VEHICLE_MODEL_IDS.length);
    await Promise.all(
      VEHICLE_MODEL_IDS.map(id =>
        this.loadModel(id).then(() => {
          loaded += 1;
          onProgress?.(loaded, VEHICLE_MODEL_IDS.length);
        }),
      ),
    );
  }

  spawn(id: string, modelId: string, route: [number, number][], duration: number, options?: { pingPong?: boolean; label?: string }): void {
    if (this.vehicles.has(id)) this.remove(id);

    const template = this.modelCache.get(modelId);
    if (!template) return;

    const mesh = template.clone();
    mesh.scale.setScalar(VEHICLE_SCALE_OVERRIDE[modelId] ?? VEHICLE_SCALE);

    const wheelMeshes = this.findWheelNodes(mesh, modelId);
    mesh.position.set(route[0][0], 0, route[0][1]);
    this.scene.add(mesh);

    const label = options?.label || id;
    // Vehicle labels are intentionally not rendered. Downstream code is
    // null-safe on labelEl, so leaving it null disables them everywhere.
    const labelEl: HTMLDivElement | null = null;

    const state: VehicleState = {
      mesh, wheelMeshes, modelId, label, labelEl, route,
      startTime: Date.now(),
      duration,
      progress: 0,
      speed: 1,
      smoothBearing: 0,
      currentPosition: [...route[0]] as [number, number],
      currentBearing: 0,
      pingPong: options?.pingPong ?? false,
      forward: true,
      animationId: 0,
      wheelRotation: 0,
      tilt: 0,
      prevBearing: 0,
      lastTime: Date.now(),
      vehicleLength: VEHICLE_HALF_LENGTH[modelId] ?? 8,
      laneState: 'LEFT' as LaneState,
      laneOffset: LEFT_LANE,
      laneChangeProgress: 0,
      laneChangeStartOffset: LEFT_LANE,
      stuckTimer: 0,
      overtakeTargetId: null,
      overtakeCooldown: 0,
      overtakeTimer: 0,
    };

    this.vehicles.set(id, state);

    // Auto-dim if dimming is active and this isn't a scenario vehicle
    if (this.dimmed && !this.scenarioIds.has(id)) {
      this.applyDim(state);
    } else if (this.dimmed && this.scenarioIds.has(id)) {
      applyTronMaterial(state.mesh, 'vehicle');
    }

    this.animate(id, state);
  }

  remove(id: string): void {
    const state = this.vehicles.get(id);
    if (!state) return;
    cancelAnimationFrame(state.animationId);
    this.scene.remove(state.mesh);
    state.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) child.geometry?.dispose();
    });
    state.labelEl?.remove();
    this.vehicles.delete(id);
  }

  removeAll(): void {
    for (const id of [...this.vehicles.keys()]) this.remove(id);
  }

  getPosition(id: string): [number, number] | null {
    return this.vehicles.get(id)?.currentPosition ?? null;
  }

  getBearing(id: string): number | null {
    return this.vehicles.get(id)?.currentBearing ?? null;
  }

  private labelsHidden = false;

  hideAllLabels(): void {
    this.labelsHidden = true;
    for (const [, state] of this.vehicles) {
      if (state.labelEl) state.labelEl.style.display = 'none';
    }
  }

  showAllLabels(): void {
    this.labelsHidden = false;
    for (const [, state] of this.vehicles) {
      if (state.labelEl) state.labelEl.style.display = '';
    }
  }

  private dimmed = false;
  private scenarioIds = new Set<string>();

  setScenarioVehicles(ids: string[]): void {
    this.scenarioIds = new Set(ids);
  }

  dimAmbient(): void {
    this.dimmed = true;
    for (const [id, state] of this.vehicles) {
      if (this.scenarioIds.has(id)) continue;
      this.applyDim(state);
    }
  }

  undimAll(): void {
    this.dimmed = false;
    this.scenarioIds.clear();
    for (const [, state] of this.vehicles) {
      applyTronMaterial(state.mesh, 'vehicle');
    }
  }

  private applyDim(state: VehicleState): void {
    const isLight = getCurrentStyle() === 'light';
    state.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material) {
        const mat = child.material as THREE.MeshPhongMaterial;
        mat.color.set(isLight ? 0xcccccc : 0x142430);
        mat.emissive.set(isLight ? 0xaaaaaa : 0x243848);
        mat.emissiveIntensity = 0.2;
        mat.transparent = true;
        mat.opacity = isLight ? 0.4 : 0.5;
      }
      if (child instanceof THREE.LineSegments && child.material) {
        const lm = child.material as THREE.LineBasicMaterial;
        lm.color.set(isLight ? 0xbbbbbb : 0x2f4a5a);
        lm.transparent = true;
        lm.opacity = isLight ? 0.4 : 0.5;
      }
    });
  }

  dispose(): void {
    this.animating = false;
    this.removeAll();
    this.modelCache.clear();
  }

  fetchRoute(origin: [number, number], destination: [number, number]): [number, number][] {
    // Use city grid pathfinding if available
    if (this.cityRouteFunc) {
      const route = this.cityRouteFunc(origin[0], origin[1], destination[0], destination[1]);
      if (route) return route as [number, number][];
    }

    // Fallback: linear interpolation
    const steps = 30;
    return Array.from({ length: steps + 1 }, (_, i) => {
      const t = i / steps;
      return [
        origin[0] + (destination[0] - origin[0]) * t,
        origin[1] + (destination[1] - origin[1]) * t,
      ] as [number, number];
    });
  }

  private async loadModel(modelId: string): Promise<void> {
    try {
      const gltf = await this.loader.loadAsync(assetUrl(`/models/${modelId}.glb`));
      applyTronMaterial(gltf.scene, 'vehicle');
      this.modelCache.set(modelId, gltf.scene);
    } catch (e) {
      console.warn(`[VehicleSystem] Failed to load ${modelId}:`, e);
    }
  }


  private findWheelNodes(root: THREE.Object3D, modelId: string): THREE.Object3D[] {
    const wheels: THREE.Object3D[] = [];

    if (modelId.startsWith('kenney-')) {
      // Kenney: wheel nodes are named "wheel-*" and are direct scene children
      // They may be Mesh or Group (if multi-primitive after spoke addition)
      for (const child of root.children) {
        if (child.name.toLowerCase().startsWith('wheel-')) {
          wheels.push(child);
        }
      }
    } else {
      // Simpoly: wheel groups contain a single mesh child — rotate that child
      root.traverse((child) => {
        const name = child.name.toLowerCase();
        if (!name.includes('wheel')) return;
        if (child instanceof THREE.Mesh) return; // skip mesh nodes themselves
        // It's a Group — find its first Mesh child
        for (const c of child.children) {
          if (c instanceof THREE.Mesh) {
            wheels.push(c);
            break;
          }
        }
      });
    }
    return wheels;
  }

  private animate(id: string, state: VehicleState): void {
    const totalLen = this.routeLength(state.route);

    const loop = () => {
      if (!this.animating || !this.vehicles.has(id)) return;

      // Global pause: hold position, keep the loop alive, resume seamlessly.
      if (pauseClock.paused) {
        state.lastTime = Date.now();
        state.animationId = requestAnimationFrame(loop);
        return;
      }

      const now = Date.now();
      const dt = Math.min((now - state.lastTime) / 1000, 0.1);
      state.lastTime = now;

      // Traffic logic: find ahead vehicle, compute speed, manage lanes
      const targetSpeed = this.updateTraffic(id, state, dt);

      // Smooth speed transitions
      if (targetSpeed < state.speed) {
        state.speed += (targetSpeed - state.speed) * 0.15;
      } else {
        state.speed += (targetSpeed - state.speed) * 0.05;
      }

      // Advance progress
      const baseSpeed = totalLen / (state.duration / 1000);
      const advance = (baseSpeed * state.speed * dt) / totalLen;

      if (state.forward) {
        state.progress += advance;
        if (state.progress >= 1) {
          if (state.pingPong) { state.progress = 1; state.forward = false; }
          else { state.progress = 1; }
        }
      } else {
        state.progress -= advance;
        if (state.progress <= 0) { state.progress = 0; state.forward = true; }
      }

      // Position on route (center line)
      const pos = this.interpolateRoute(state.route, state.progress);

      // Bearing
      const targetBearing = this.getRouteBearing(state.route, state.progress, state.forward);
      let bearingDiff = targetBearing - state.smoothBearing;
      if (bearingDiff > 180) bearingDiff -= 360;
      if (bearingDiff < -180) bearingDiff += 360;
      const lerpRate = Math.abs(bearingDiff) > 45 ? 0.12 : 0.08;
      state.smoothBearing += bearingDiff * lerpRate;
      state.currentBearing = state.smoothBearing;

      // Lean into turns
      const turnRate = bearingDiff * lerpRate;
      const targetTilt = Math.max(-0.08, Math.min(0.08, -turnRate * 0.02));
      state.tilt += (targetTilt - state.tilt) * 0.06;

      // Corner braking
      const turnMagnitude = Math.abs(bearingDiff);
      if (turnMagnitude > 20) {
        state.speed = Math.min(state.speed, Math.max(0.3, 1 - turnMagnitude / 120));
      }

      // World position = route center + lane offset perpendicular to bearing
      const bearingRad = (state.smoothBearing * Math.PI) / 180;
      const perpX = Math.cos(bearingRad);
      const perpZ = -Math.sin(bearingRad);
      const offsetX = perpX * state.laneOffset;
      const offsetZ = perpZ * state.laneOffset;

      // Pivot: position represents front axle, shift mesh backward
      const pivotOffset = state.vehicleLength * 0.4;
      const backX = -Math.sin(bearingRad) * pivotOffset;
      const backZ = -Math.cos(bearingRad) * pivotOffset;

      state.currentPosition = [pos[0] + offsetX, pos[1] + offsetZ];
      state.mesh.position.set(pos[0] + offsetX + backX, 0, pos[1] + offsetZ + backZ);

      // Model rotation: face the bearing direction
      const rotOffset = MODEL_ROTATION_OFFSET[state.modelId] || 0;
      state.mesh.rotation.y = bearingRad + rotOffset;
      state.mesh.rotation.z = state.tilt;

      // Wheels
      if (state.wheelMeshes.length > 0) {
        state.wheelRotation += baseSpeed * state.speed * dt * 0.15;
        const isKenney = state.modelId.startsWith('kenney-');
        for (const wheel of state.wheelMeshes) {
          if (isKenney) wheel.rotation.x = state.wheelRotation;
          else wheel.rotation.z = state.wheelRotation;
        }
      }

      // Label — hidden during scenario, otherwise visible at zoom >= 15.5
      if (state.labelEl && this.scene.map) {
        if (this.labelsHidden) {
          state.labelEl.style.display = 'none';
        } else {
          const zoom = this.scene.map.getZoom();
          if (zoom < 15.5) {
            state.labelEl.style.display = 'none';
          } else {
            state.labelEl.style.display = '';
            const lngLat = this.scene.worldToLngLat(state.mesh.position);
            const point = this.scene.map.project(lngLat);
            state.labelEl.style.left = point.x + 'px';
            state.labelEl.style.top = (point.y - 30) + 'px';
          }
        }
      }

      if (state.pingPong || state.progress < 1) {
        state.animationId = requestAnimationFrame(loop);
      }
    };

    state.animationId = requestAnimationFrame(loop);
  }

  // ─── TRAFFIC SYSTEM ───

  private updateTraffic(selfId: string, self: VehicleState, dt: number): number {
    const bRad = (self.smoothBearing * Math.PI) / 180;
    const fwdX = Math.sin(bRad);
    const fwdZ = Math.cos(bRad);

    // Detect if on a turn
    const isTurning = this.detectTurn(self);

    // Find vehicle ahead in same lane
    const ahead = this.findVehicleAhead(selfId, self, fwdX, fwdZ);

    // Compute base speed from following distance
    let targetSpeed = 1.0;
    if (ahead) {
      const minDist = self.vehicleLength + ahead.state.vehicleLength + SAFE_GAP;
      if (ahead.dist < minDist * 0.7) {
        targetSpeed = 0;
      } else if (ahead.dist < minDist) {
        targetSpeed = Math.min(targetSpeed, ahead.state.speed * 0.8);
      } else if (ahead.dist < minDist * 2.5) {
        const t = (ahead.dist - minDist) / (minDist * 1.5);
        targetSpeed = Math.min(targetSpeed, ahead.state.speed + Math.max(0, t) * (1 - ahead.state.speed));
      }
    }

    // Lane state machine
    if (self.overtakeCooldown > 0) self.overtakeCooldown -= dt;

    switch (self.laneState) {
      case 'LEFT': {
        // Stuck = there's a vehicle ahead within close range that's causing us to slow
        if (ahead && ahead.dist < (self.vehicleLength + ahead.state.vehicleLength + SAFE_GAP * 3)) {
          self.stuckTimer += dt;
        } else {
          self.stuckTimer = 0;
        }

        if (self.stuckTimer > STUCK_TIME && ahead && !isTurning && self.overtakeCooldown <= 0) {
          if (!this.hasOncoming(self, fwdX, fwdZ) && !this.rightLaneBlocked(selfId, self, fwdX, fwdZ)) {
            self.laneState = 'CHANGING_RIGHT';
            self.laneChangeProgress = 0;
            self.laneChangeStartOffset = self.laneOffset;
            self.overtakeTargetId = ahead.id;
            self.overtakeTimer = 0;
            self.stuckTimer = 0;
          }
        }
        break;
      }
      case 'CHANGING_RIGHT': {
        self.laneChangeProgress += dt / LANE_CHANGE_TIME;
        if (this.hasOncoming(self, fwdX, fwdZ)) {
          self.laneState = 'CHANGING_LEFT';
          self.laneChangeProgress = 0;
          self.laneChangeStartOffset = self.laneOffset;
          self.overtakeCooldown = OVERTAKE_COOLDOWN;
        } else if (self.laneChangeProgress >= 1) {
          self.laneChangeProgress = 1;
          self.laneState = 'RIGHT';
        }
        targetSpeed = 1.0; // full speed during lane change
        break;
      }
      case 'RIGHT': {
        self.overtakeTimer += dt;
        if (this.hasClearedTarget(selfId, self, fwdX, fwdZ)) {
          self.laneState = 'CHANGING_LEFT';
          self.laneChangeProgress = 0;
          self.laneChangeStartOffset = self.laneOffset;
        } else if (this.hasOncoming(self, fwdX, fwdZ) || self.overtakeTimer > 12) {
          self.laneState = 'CHANGING_LEFT';
          self.laneChangeProgress = 0;
          self.laneChangeStartOffset = self.laneOffset;
          self.overtakeCooldown = OVERTAKE_COOLDOWN;
        }
        targetSpeed = 1.0; // full speed while overtaking
        break;
      }
      case 'CHANGING_LEFT': {
        self.laneChangeProgress += dt / LANE_CHANGE_TIME;
        if (self.laneChangeProgress >= 1) {
          self.laneChangeProgress = 1;
          self.laneState = 'LEFT';
          self.overtakeTargetId = null;
          self.overtakeCooldown = OVERTAKE_COOLDOWN;
          self.overtakeTimer = 0;
        }
        break;
      }
    }

    // Update lane offset with smoothstep
    this.updateLaneOffset(self, dt);

    // Hard minimum distance enforcement (all vehicles regardless of lane)
    targetSpeed = this.enforceHardMin(selfId, self, targetSpeed, fwdX, fwdZ);

    return targetSpeed;
  }

  private detectTurn(self: VehicleState): boolean {
    const curr = this.getRouteBearing(self.route, self.progress, self.forward);
    const future = this.getRouteBearing(self.route, Math.min(1, self.progress + 0.02), self.forward);
    let diff = future - curr;
    if (diff > 180) diff -= 360;
    if (diff < -180) diff += 360;
    return Math.abs(diff) > TURN_THRESHOLD;
  }

  private findVehicleAhead(selfId: string, self: VehicleState, fwdX: number, fwdZ: number): { id: string; state: VehicleState; dist: number } | null {
    let best: { id: string; state: VehicleState; dist: number } | null = null;

    for (const [otherId, other] of this.vehicles) {
      if (otherId === selfId) continue;
      const dx = other.currentPosition[0] - self.currentPosition[0];
      const dz = other.currentPosition[1] - self.currentPosition[1];
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (dist > LOOK_AHEAD) continue;

      const ahead = dx * fwdX + dz * fwdZ;
      if (ahead <= 0) continue;

      const lateral = dx * (-fwdZ) + dz * fwdX;
      const laneDiff = Math.abs(lateral - (other.laneOffset - self.laneOffset));
      if (laneDiff > 10) continue;

      const otherRad = (other.smoothBearing * Math.PI) / 180;
      const dirDot = fwdX * Math.sin(otherRad) + fwdZ * Math.cos(otherRad);
      if (dirDot < 0.5) continue;

      if (!best || ahead < best.dist) {
        best = { id: otherId, state: other, dist: ahead };
      }
    }
    return best;
  }

  private hasOncoming(self: VehicleState, fwdX: number, fwdZ: number): boolean {
    for (const [, other] of this.vehicles) {
      const dx = other.currentPosition[0] - self.currentPosition[0];
      const dz = other.currentPosition[1] - self.currentPosition[1];
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (dist > ONCOMING_RANGE) continue;

      const ahead = dx * fwdX + dz * fwdZ;
      if (ahead <= 5) continue; // must be clearly ahead

      const otherRad = (other.smoothBearing * Math.PI) / 180;
      const dirDot = fwdX * Math.sin(otherRad) + fwdZ * Math.cos(otherRad);
      if (dirDot > -0.5) continue; // must be clearly oncoming

      // Check if in the right lane area (where we'd overtake)
      const lateral = dx * (-fwdZ) + dz * fwdX;
      if (lateral > -5 && lateral < 18) {
        return true;
      }
    }
    return false;
  }

  private rightLaneBlocked(selfId: string, self: VehicleState, fwdX: number, fwdZ: number): boolean {
    for (const [otherId, other] of this.vehicles) {
      if (otherId === selfId) continue;
      if (other.laneState !== 'RIGHT' && other.laneState !== 'CHANGING_RIGHT') continue;

      const dx = other.currentPosition[0] - self.currentPosition[0];
      const dz = other.currentPosition[1] - self.currentPosition[1];
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (dist > LOOK_AHEAD) continue;

      const otherRad = (other.smoothBearing * Math.PI) / 180;
      const dirDot = fwdX * Math.sin(otherRad) + fwdZ * Math.cos(otherRad);
      if (dirDot > 0.5) return true;
    }
    return false;
  }

  private hasClearedTarget(_selfId: string, self: VehicleState, fwdX: number, fwdZ: number): boolean {
    // Stay in right lane until ALL same-direction left-lane vehicles nearby are behind us
    for (const [, other] of this.vehicles) {
      if (other === self) continue;
      if (other.laneState !== 'LEFT') continue;

      const dx = other.currentPosition[0] - self.currentPosition[0];
      const dz = other.currentPosition[1] - self.currentPosition[1];
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (dist > LOOK_AHEAD) continue;

      const otherRad = (other.smoothBearing * Math.PI) / 180;
      const dirDot = fwdX * Math.sin(otherRad) + fwdZ * Math.cos(otherRad);
      if (dirDot < 0.5) continue; // not same direction

      // Is this vehicle still ahead or beside us?
      const ahead = dx * fwdX + dz * fwdZ;
      const clearDist = (self.vehicleLength + other.vehicleLength) * CLEAR_MARGIN;
      if (ahead > -clearDist) return false; // haven't cleared this one yet
    }
    return true;
  }

  private updateLaneOffset(self: VehicleState, dt: number): void {
    let target: number;
    switch (self.laneState) {
      case 'LEFT':
        target = LEFT_LANE;
        break;
      case 'RIGHT':
        target = RIGHT_LANE;
        break;
      case 'CHANGING_RIGHT': {
        const t = self.laneChangeProgress * self.laneChangeProgress * (3 - 2 * self.laneChangeProgress);
        target = self.laneChangeStartOffset + (RIGHT_LANE - self.laneChangeStartOffset) * t;
        break;
      }
      case 'CHANGING_LEFT': {
        const t = self.laneChangeProgress * self.laneChangeProgress * (3 - 2 * self.laneChangeProgress);
        target = self.laneChangeStartOffset + (LEFT_LANE - self.laneChangeStartOffset) * t;
        break;
      }
    }
    self.laneOffset += (target - self.laneOffset) * Math.min(1, dt * 8);
  }

  private enforceHardMin(selfId: string, self: VehicleState, speed: number, fwdX: number, fwdZ: number): number {
    for (const [otherId, other] of this.vehicles) {
      if (otherId === selfId) continue;
      const dx = other.currentPosition[0] - self.currentPosition[0];
      const dz = other.currentPosition[1] - self.currentPosition[1];
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (dist > LOOK_AHEAD) continue;

      // Only enforce against same-direction vehicles in same lane
      const otherRad = (other.smoothBearing * Math.PI) / 180;
      const dirDot = fwdX * Math.sin(otherRad) + fwdZ * Math.cos(otherRad);
      if (dirDot < 0.3) continue; // skip oncoming/perpendicular

      const lateral = dx * (-fwdZ) + dz * fwdX;
      const laneDiff = Math.abs(lateral - (other.laneOffset - self.laneOffset));
      if (laneDiff > 12) continue; // different lane

      const ahead = dx * fwdX + dz * fwdZ;
      if (ahead <= 0) continue;

      const effectiveDist = ahead - self.vehicleLength - other.vehicleLength;
      if (effectiveDist < HARD_MIN_DIST) return 0;
    }
    return speed;
  }

  private getRouteBearing(route: [number, number][], t: number, forward: boolean): number {
    const n = route.length - 1;
    if (n < 1) return 0;

    // Use a small look-ahead for bearing calculation (smooths through corners)
    const lookAhead = 0.015;
    const t1 = Math.max(0, Math.min(1, t - lookAhead * 0.5));
    const t2 = Math.max(0, Math.min(1, t + lookAhead));

    const p1 = this.interpolateRouteRaw(route, t1);
    const p2 = this.interpolateRouteRaw(route, t2);

    let dx = p2[0] - p1[0];
    let dz = p2[1] - p1[1];

    if (Math.abs(dx) < 0.001 && Math.abs(dz) < 0.001) {
      const seg = Math.min(Math.floor(t * n), n - 1);
      dx = route[Math.min(seg + 1, n)][0] - route[seg][0];
      dz = route[Math.min(seg + 1, n)][1] - route[seg][1];
    }

    let bearing = (Math.atan2(dx, dz) * 180) / Math.PI;
    if (!forward) bearing += 180;
    if (bearing > 180) bearing -= 360;
    if (bearing < -180) bearing += 360;
    return bearing;
  }

  private interpolateRoute(route: [number, number][], t: number): [number, number] {
    // Linear interpolation — vehicle stays on the road path exactly
    return this.interpolateRouteRaw(route, t);
  }

  private interpolateRouteRaw(route: [number, number][], t: number): [number, number] {
    const n = route.length - 1;
    const seg = Math.min(Math.floor(t * n), n - 1);
    const segT = (t * n) - seg;
    const a = route[seg];
    const b = route[Math.min(seg + 1, route.length - 1)];
    return [a[0] + (b[0] - a[0]) * segT, a[1] + (b[1] - a[1]) * segT];
  }

  private routeLength(route: [number, number][]): number {
    let len = 0;
    for (let i = 0; i < route.length - 1; i++) {
      const dx = route[i + 1][0] - route[i][0];
      const dz = route[i + 1][1] - route[i][1];
      len += Math.sqrt(dx * dx + dz * dz);
    }
    return len;
  }
}
